import { UltrasoundEvidenceImport } from './UltrasoundEvidenceImport';
import { documentReviewUrl } from './api';
import { bulkCandidates } from './suggestions';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type Suggestion = {
  id: string;
  field: string;
  label: string;
  confidence: number;
  method?: string;
  evidence_span: string;
  revision_id: string;
};
type Review = {
  source_hash: string;
  version: number;
  revision_id: string;
  patient_id: string | null;
  data: {
    phase?: string;
    kind?: string;
    subject?: string;
    tags?: string[];
    patient_match?: { revision_id: string; patient_id: string; source_hash: string };
  };
  suggestions: Suggestion[];
  patient_matches?: Array<{
    patient_id: string;
    confidence: number;
    matched_fields: string[];
    display_name?: string;
    medical_record_number?: string;
    date_of_birth?: string;
  }>;
  clinical_proposals?: Array<{
    id: string;
    status: string;
    metric: string;
    value: string;
    unit: string;
    evidence_span: string;
  }>;
  taxonomy: Record<string, string[]>;
};
export function DocumentClassificationPanel({
  kind,
  documentId,
}: {
  kind: 'uploaded' | 'generated';
  documentId: string;
}) {
  const { organization, user } = useAuth();
  const org = organization?.organization_id;
  const cache = useQueryClient();
  const url = documentReviewUrl(org, `/${kind}/${encodeURIComponent(documentId)}`);
  const key = ['document-review', org, kind, documentId];
  const query = useQuery({
    queryKey: key,
    enabled: !!org,
    queryFn: async () => (await clientApi.get<Review>(url)).data,
  });
  const preferenceKey = `document-review-bulk:${org}:${user?.id}`;
  const [bulkEnabled, setBulkEnabled] = useState(() => localStorage.getItem(preferenceKey) === 'true');
  const bulk = useMutation({
    mutationFn: async () => {
      let version = query.data!.version;
      for (const suggestion of bulkCandidates(query.data!.suggestions)) {
        const response = await clientApi.post<{ version: number }>(url, {
          expected_version: version,
          revision_id: query.data!.revision_id,
          source_hash: query.data!.source_hash,
          reason: 'Explicit bulk review of high-confidence document labels',
          action: 'accept',
          suggestion_id: suggestion.id,
        });
        version = response.data.version;
      }
    },
    onSettled: () => cache.invalidateQueries({ queryKey: key }),
  });
  const [reason, setReason] = useState('Reviewed against the original document');
  const save = useMutation({
    mutationFn: async (input: Record<string, unknown>) =>
      clientApi.post(url, {
        expected_version: query.data!.version,
        revision_id: query.data!.revision_id,
        source_hash: query.data!.source_hash,
        reason,
        ...input,
      }),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: key });
      cache.invalidateQueries({ queryKey: ['classification-queue', org] });
      cache.invalidateQueries({ queryKey: ['document-review-statistics', org] });
    },
  });
  const clinical = useMutation({
    mutationFn: async (input: { proposal_id: string; action: 'accept' | 'reject' }) =>
      clientApi.post(`${url}/clinical-evidence`, {
        ...input,
        expected_version: query.data!.version,
        revision_id: query.data!.revision_id,
        source_hash: query.data!.source_hash,
        patient_match_confirmed: true,
        reason,
      }),
    onSuccess: () => cache.invalidateQueries({ queryKey: key }),
  });
  if (!query.data)
    return <p>{query.isError ? 'Document review could not load.' : 'Loading document classification…'}</p>;
  const review = query.data;
  return (
    <section className="space-y-3 rounded border p-4" aria-label="Document classification">
      <h3 className="font-semibold">Document classification</h3>
      <label className="block text-sm">
        <input
          type="checkbox"
          checked={bulkEnabled}
          onChange={(e) => {
            setBulkEnabled(e.target.checked);
            localStorage.setItem(preferenceKey, String(e.target.checked));
          }}
        />{' '}
        Enable explicit bulk acceptance of labels ≥90% for my account on this browser
      </label>
      {bulkEnabled && (
        <Button
          variant="outline"
          disabled={bulk.isPending || save.isPending || !bulkCandidates(review.suggestions).length}
          onClick={() => bulk.mutate()}>
          Accept reviewed labels ≥90%
        </Button>
      )}
      {bulk.isError && (
        <p role="alert">Bulk review stopped. Saved decisions are retained; review the remaining suggestions.</p>
      )}
      <p className="text-sm text-muted-foreground">
        Labels organize documents. Clinical results require separate clinical review.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {Object.entries(review.taxonomy).map(([field, values]) => (
          <label key={field} className="text-sm capitalize">
            {field}
            <select
              aria-label={`Document ${field}`}
              className="block w-full rounded border p-2"
              value={review.data[field as 'phase' | 'kind' | 'subject'] ?? ''}
              disabled={bulk.isPending || save.isPending}
              onChange={(e) => save.mutate({ action: 'set', field, value: e.target.value })}>
              <option value="">Unclassified</option>
              {values.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <label className="block text-sm">
        Review note
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <p>Classification tags: {review.data.tags?.join(', ') || 'None'}</p>
      {review.patient_matches?.map((match) => (
        <p key={match.patient_id} className="text-sm">
          Candidate {match.display_name || match.patient_id} · MRN {match.medical_record_number} · DOB{' '}
          {match.date_of_birth}. Saved source identifier evidence:{' '}
          {match.matched_fields.join(', ') || 'No exact identifiers found'} · {Math.round(match.confidence * 100)}% rule
          confidence. Confirm identifiers in the original document.
        </p>
      ))}
      {!review.patient_id &&
        kind === 'uploaded' &&
        review.patient_matches?.map((match) => (
          <Button
            key={match.patient_id}
            variant="outline"
            disabled={save.isPending || bulk.isPending}
            onClick={() => save.mutate({ action: 'set', field: 'patient_match', value: match.patient_id })}>
            I reviewed name, DOB and MRN: assign to {match.display_name || match.medical_record_number}
          </Button>
        ))}
      {review.patient_id ? (
        <div>
          <p>
            {review.data.patient_match?.revision_id === review.revision_id &&
            review.data.patient_match.patient_id === review.patient_id &&
            review.data.patient_match.source_hash === review.source_hash
              ? 'Patient match reviewed for this revision.'
              : 'Patient match needs review against the original name, DOB, and MRN.'}
          </p>
          <Button
            variant="outline"
            disabled={bulk.isPending || save.isPending || reason.length < 3}
            onClick={() => save.mutate({ action: 'set', field: 'patient_match', value: review.patient_id })}>
            Confirm assigned patient match
          </Button>
        </div>
      ) : (
        <p>Unmatched Inbox document: assign a patient after reviewing identifiers. Labels do not assign a patient.</p>
      )}
      {review.suggestions.map((s) => (
        <div key={s.id} className="rounded border border-dashed p-3">
          <p>
            {s.field}: <strong>{s.label}</strong> · {Math.round(s.confidence * 100)}%{' '}
            {s.method === 'MODEL' ? 'model' : 'rule'} confidence
          </p>
          <blockquote className="whitespace-pre-wrap text-sm">{s.evidence_span}</blockquote>
          <div className="flex gap-2">
            <Button
              disabled={bulk.isPending || save.isPending || reason.length < 3}
              onClick={() => save.mutate({ action: 'accept', suggestion_id: s.id })}>
              Accept
            </Button>
            <Button
              variant="outline"
              disabled={bulk.isPending || save.isPending || reason.length < 3}
              onClick={() => save.mutate({ action: 'reject', suggestion_id: s.id })}>
              Reject
            </Button>
          </div>
        </div>
      ))}
      {!!review.clinical_proposals?.length && (
        <section className="space-y-2 border-t pt-3">
          <h4 className="font-semibold">Ultrasound report evidence</h4>
          <p>
            Review exact source values first. Accepted evidence may then be explicitly imported into a selected
            pregnancy, fetus and encounter below; nothing is imported automatically.
          </p>
          {review.clinical_proposals.map((proposal) => (
            <div key={proposal.id} className="border p-2">
              <p>
                {proposal.metric}: {proposal.value} {proposal.unit} · {proposal.status}
              </p>
              <blockquote className="whitespace-pre-wrap">{proposal.evidence_span}</blockquote>
              {proposal.status === 'PENDING_REVIEW' && (
                <div className="flex gap-2">
                  <Button
                    disabled={clinical.isPending || save.isPending || !review.patient_id}
                    onClick={() => clinical.mutate({ proposal_id: proposal.id, action: 'accept' })}>
                    Accept source evidence
                  </Button>
                  <Button
                    variant="outline"
                    disabled={clinical.isPending || save.isPending || !review.patient_id}
                    onClick={() => clinical.mutate({ proposal_id: proposal.id, action: 'reject' })}>
                    Reject evidence
                  </Button>
                </div>
              )}
            </div>
          ))}
          {clinical.isError && (
            <p role="alert">
              Clinical review was not saved. Confirm the patient match and clinical-review permission, then refresh
              changed evidence.
            </p>
          )}
        </section>
      )}
      {kind === 'uploaded' && org && (
        <UltrasoundEvidenceImport
          organizationId={org}
          documentId={documentId}
          source={review}
          onImported={() => {
            void cache.invalidateQueries({ queryKey: key });
          }}
        />
      )}
      {save.isError && (
        <p role="alert">Review was not saved. Refresh if the document changed; your note is retained.</p>
      )}
    </section>
  );
}
